import { View, XStack, YStack, Text, styled } from 'tamagui'
import { HeroIcon } from '../HeroIcon'
import { RangeInput } from '../RangeInput'
import { AutoPlaySwitch } from './AutoPlaySwitch'
import { IconProp } from '@fortawesome/fontawesome-svg-core'
import { useMemo } from 'react'
import { Platform } from 'react-native'

const BUFFERED_PLATFORM_STYLES = (() => {
  const isIOS = Platform.OS === 'ios'
  const translateX = isIOS ? 4 : 15.5
  const translateY = isIOS ? -2.3 : -1.3
  const height = isIOS ? 4 : 2.3
  return {
    transform: [{ translateY }, { translateX }],
    height,
  }
})()

interface IBottomControlsProps {
  controlBottomPosition: number
  isDesktop: boolean
  isMobile: boolean
  onPlayPause: () => void
  isPlaying: boolean
  bottomControlsSize: number
  onToggleMute: () => void
  volumeIcon: IconProp
  showVolumeSelector: boolean
  volume: number
  onVolumeChange: (value: number) => void
  currentTime: number
  isAutoPlayActive: boolean
  autoPlayIcon: IconProp
  onAutoPlay: () => void
  onOpenConfigs: () => void
  onPictureInPicture: () => void
  onFullScreen: () => void
  isFullScreenMode: boolean
  rangeColor: string
  currentTimePercentage: number
  onProgressChange: (value: number) => void
  bufferedPercentage: number
  labels: {
    playPause: string
    mute: string
    openQualityMenu: string
    pictureInPicture: string
    fullscreen: string
  }
}

export function BottomControls({
  controlBottomPosition,
  isDesktop,
  isMobile,
  onPlayPause,
  isPlaying,
  bottomControlsSize,
  onToggleMute,
  volumeIcon,
  showVolumeSelector,
  volume,
  onVolumeChange,
  currentTime,
  isAutoPlayActive,
  autoPlayIcon,
  onAutoPlay,
  onOpenConfigs,
  onPictureInPicture,
  onFullScreen,
  isFullScreenMode,
  rangeColor,
  currentTimePercentage,
  onProgressChange,
  bufferedPercentage,
  labels,
}: IBottomControlsProps) {
  const playPauseIcon: IconProp = isPlaying ? ['fas', 'pause'] : ['fas', 'play']

  const formattedTime = useMemo(() => {
    const hours = Math.floor(currentTime / 3600)
    const minutes = Math.floor((currentTime % 3600) / 60)
    const seconds = Math.floor(currentTime % 60)

    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`
    }

    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`
  }, [currentTime])

  const videoTime = (
    <XStack>
      <Text
        color="$text-on-media"
        ff="$mulishRegular"
        fontSize="$font-size-xxs"
      >
        {formattedTime}
      </Text>
    </XStack>
  )

  return (
    <View
      position="absolute"
      bottom={controlBottomPosition}
      left={0}
      right={0}
      paddingHorizontal="$spacing-inset-xs"
      zIndex={10}
      $gtMd={{
        flexDirection: 'column-reverse',
      }}
    >
      <XStack
        alignItems="center"
        justifyContent="space-between"
        zIndex={1}
        paddingLeft="$spacing-inset-xs"
      >
        {isDesktop && (
          <XStack alignItems="center" gap="$spacing-inset-xs">
            <MediaControl onPress={onPlayPause} aria-label={labels.playPause}>
              <HeroIcon
                icon={playPauseIcon}
                size={bottomControlsSize}
                color="$text-on-media"
              />
            </MediaControl>

            <YStack position="relative">
              <MediaControl
                onPress={onToggleMute}
                aria-label={labels.mute}
                position="relative"
              >
                <HeroIcon
                  icon={volumeIcon}
                  size={bottomControlsSize}
                  color="$text-on-media"
                />
                {showVolumeSelector && isDesktop && (
                  <View position="absolute" bottom={50} left={20}>
                    <RangeInput
                      rangeColor="$text-on-media"
                      value={volume}
                      onValueChange={onVolumeChange}
                    />
                  </View>
                )}
              </MediaControl>
            </YStack>
          </XStack>
        )}

        {isMobile && videoTime}

        <XStack alignItems="center" gap="$spacing-inset-xs">
          {isDesktop && (
            <AutoPlaySwitch
              isActive={isAutoPlayActive}
              icon={autoPlayIcon}
              onPress={onAutoPlay}
            />
          )}
          <MediaControl
            onPress={onOpenConfigs}
            aria-label={labels.openQualityMenu}
          >
            <HeroIcon
              icon={['fal', 'cog']}
              size={bottomControlsSize}
              color="$text-on-media"
            />
          </MediaControl>

          <MediaControl
            onPress={onPictureInPicture}
            aria-label={labels.pictureInPicture}
          >
            <HeroIcon
              icon={'picture-in-picture'}
              size={bottomControlsSize}
              color="$text-on-media"
            />
          </MediaControl>

          <MediaControl onPress={onFullScreen} aria-label={labels.fullscreen}>
            <HeroIcon
              icon={isFullScreenMode ? ['fas', 'compress'] : ['fas', 'expand']}
              size={bottomControlsSize}
              color="$text-on-media"
            />
          </MediaControl>
        </XStack>
      </XStack>
      <XStack>
        <XStack
          width="100%"
          position="relative"
          alignItems="center"
          gap="$spacing-inset-xxs"
          zIndex={1}
        >
          <View width="100%" position="relative" overflow="hidden">
            <RangeInput
              rangeColor={rangeColor}
              foregroundColor="rgba(207, 207, 207, 0.40)"
              value={currentTimePercentage}
              onSlidingComplete={onProgressChange}
            />
            <View
              position="absolute"
              width={`${Math.round(bufferedPercentage)}%`}
              height={BUFFERED_PLATFORM_STYLES.height}
              backgroundColor="$neutral-500"
              borderRadius="$border-radius-pill"
              top="50%"
              transform={BUFFERED_PLATFORM_STYLES.transform}
              zIndex={-10}
            />
          </View>
          {isDesktop && videoTime}
        </XStack>
      </XStack>
    </View>
  )
}

const MediaControl = styled(View, {
  role: 'button',
  cursor: 'pointer',
  hoverStyle: {
    backgroundColor: 'rgba(0, 0, 0, 0.1)',
  },
  padding: '$spacing-inset-xxs',
})
